import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function EventCard({ event }) {
  const [isSaved, setIsSaved] = useState(false);

  // Normalize field names to handle PascalCase and camelCase/snake_case
  const id = event.ID || event.id;
  const title = event.Title || event.title || '';
  const location = event.Location || event.location || '';
  const city = event.City || event.city || '';
  const startTime = event.StartTime || event.start_time || event.startTime;
  const currentAttendees = event.CurrentAttendees || event.current_attendees || event.currentAttendees || 0;
  const maxAttendees = event.MaxAttendees || event.max_attendees || event.maxAttendees;
  const imageUrl = event.ImageURL || event.image_url || event.imageUrl || event.imageURL;
  const attendees = event.Attendees || event.attendees || [];

  // Parse date
  const date = startTime ? new Date(startTime) : null;
  const monthAbbr = date
    ? date.toLocaleString('en-US', { month: 'short' }).toUpperCase()
    : '';
  const dayNumber = date ? date.getDate() : '';
  const timeString = date
    ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    : '';

  // Urgency
  const spotsLeft = maxAttendees ? maxAttendees - currentAttendees : null;
  const showUrgency = maxAttendees && spotsLeft !== null && spotsLeft <= 5;
  const isFull = spotsLeft !== null && spotsLeft <= 0;

  // Display location
  const displayLocation = [location, city].filter(Boolean).join(', ');

  // Avatar gradient colors for social proof
  const avatarGradients = [
    'from-primary-300 to-primary-500',
    'from-accent-300 to-accent-500',
    'from-primary-400 to-accent-400',
    'from-accent-400 to-primary-300',
  ];

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSaved((prev) => !prev);
  };

  return (
    <Link
      to={`/events/${id}`}
      className="group block bg-white rounded-2xl shadow-soft-sm border border-gray-100 hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden"
    >
      {/* Image container */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`w-full h-full bg-gradient-to-br from-primary-200 via-primary-300 to-accent-200 flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}>
          <svg className="w-10 h-10 text-primary-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>

        {/* Date chip */}
        {date && (
          <div className="absolute top-3 left-3 flex flex-col items-center bg-white/85 backdrop-blur-sm rounded-xl px-2.5 py-1.5 shadow-soft-sm">
            <span className="text-[10px] font-bold text-primary-600 leading-tight tracking-wide">
              {monthAbbr}
            </span>
            <span className="text-lg font-bold text-surface-900 leading-tight">
              {dayNumber}
            </span>
          </div>
        )}

        {/* Urgency badge */}
        {showUrgency && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-accent-500 text-white text-[11px] font-bold tracking-wide shadow-soft-sm">
              {isFull ? 'FULL' : `${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left`}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Time */}
        {timeString && (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
            <span className="text-body-sm text-surface-500 font-medium">
              {timeString}
            </span>
          </div>
        )}

        {/* Title */}
        <h3 className="text-h3 text-surface-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200 mb-2">
          {title}
        </h3>

        {/* Location */}
        {displayLocation && (
          <div className="flex items-center gap-1.5 text-body-sm text-surface-400 mb-3">
            <svg
              className="w-4 h-4 flex-shrink-0 text-surface-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="truncate">{displayLocation}</span>
          </div>
        )}

        {/* Footer: Social proof + Save */}
        <div className="flex items-center justify-between pt-2 border-t border-surface-100">
          {/* Social proof avatars */}
          <div className="flex items-center">
            <div className="flex -space-x-2">
              {[...Array(Math.min(currentAttendees, 3))].map((_, i) => {
                const attendee = attendees[i];
                const gradient = avatarGradients[i % avatarGradients.length];
                return (
                  <div
                    key={i}
                    className={`w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br ${gradient} flex items-center justify-center`}
                  >
                    {attendee?.avatar || attendee?.Avatar ? (
                      <img
                        src={attendee.avatar || attendee.Avatar}
                        alt=""
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-[10px] font-bold text-white">
                        {attendee?.username?.[0]?.toUpperCase() ||
                          attendee?.Username?.[0]?.toUpperCase() ||
                          ''}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {currentAttendees > 0 && (
              <span className="ml-2 text-body-sm text-surface-500 font-medium">
                {currentAttendees} going
              </span>
            )}
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            className="p-1.5 rounded-lg hover:bg-surface-50 transition-colors duration-150"
            aria-label={isSaved ? 'Remove from saved' : 'Save event'}
          >
            <svg
              className={`w-5 h-5 transition-colors duration-200 ${
                isSaved
                  ? 'text-accent-500 fill-accent-500'
                  : 'text-surface-300 group-hover:text-surface-400'
              }`}
              fill={isSaved ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );
}

export default EventCard;
